import { useEffect, useMemo, useState } from 'react'
import { Printer } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Label } from '../../lib/shadcn/label'
import { RadioGroup, RadioGroupItem } from '../../lib/shadcn/radio-group'
import { cn } from '../../lib/shadcn/utils'
import { STATIONS } from '../../utils/stations'
import {
  generateStationRecipesPdf,
  printableRecipes,
  type StationChoice,
  type StationPlan,
  type StationRecipe,
} from '../../utils/stationRecipesPdf'
import { HelpText } from '../../components/HelpText'

type Amounts = 'plan' | 'single'

const CHOICES: StationChoice[] = [...STATIONS, null]

function choiceKey(s: StationChoice): string {
  return s ?? 'none'
}

/**
 * Prints recipes grouped by production station: either the flavors in the
 * production plan scaled to its batches, or one batch of every recipe.
 */
export default function PrintRecipesDialog({
  open,
  onOpenChange,
  recipes,
  plan,
  loading,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  recipes: StationRecipe[]
  plan: StationPlan | null
  loading: boolean
}) {
  const [amounts, setAmounts] = useState<Amounts>('plan')
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(CHOICES.map(choiceKey)))

  useEffect(() => {
    if (open && !plan) setAmounts('single')
  }, [open, plan])

  const usePlan = amounts === 'plan' && !!plan
  // Recipes that would print in this mode (before the station filter).
  const inScope = useMemo(
    () => printableRecipes(recipes, usePlan ? plan : null),
    [recipes, plan, usePlan],
  )

  const countFor = (s: StationChoice) => inScope.filter(r => (r.station ?? null) === s).length
  const noStation = inScope.filter(r => !r.station).map(r => r.label_name)
  const selected = CHOICES.filter(s => chosen.has(choiceKey(s)))
  const printCount = inScope.filter(r => selected.includes(r.station as StationChoice)).length

  const toggle = (s: StationChoice, on: boolean) =>
    setChosen(prev => {
      const next = new Set(prev)
      if (on) next.add(choiceKey(s))
      else next.delete(choiceKey(s))
      return next
    })

  const print = () => {
    generateStationRecipesPdf({ recipes, stations: selected, plan: usePlan ? plan : null })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Print recipes by station</DialogTitle>
          <DialogDescription><HelpText>
            Each station starts on its own page, with a checkbox and the ingredients for every
            recipe made there. Set a recipe&apos;s station in its Recipe dialog. Percentage blends
            aren&apos;t printed; the flavors they&apos;re mixed from note how much goes into them.
          </HelpText></DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-medium">Amounts</p>
            <RadioGroup
              value={usePlan ? 'plan' : 'single'}
              onValueChange={v => setAmounts(v as Amounts)}
              className="gap-2"
            >
              {(
                [
                  {
                    value: 'plan',
                    title: 'This production plan',
                    description:
                      'Only the flavors in the plan, with totals for its batches.',
                    disabled: !plan,
                  },
                  {
                    value: 'single',
                    title: 'One batch of every recipe',
                    description: 'Every recipe at its base batch, for a recipe binder.',
                    disabled: false,
                  },
                ] as const
              ).map(opt => (
                <Label
                  key={opt.value}
                  htmlFor={`print-recipes-${opt.value}`}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                    (usePlan ? 'plan' : 'single') === opt.value
                      ? 'border-primary bg-accent'
                      : 'border-border hover:bg-accent/50',
                    opt.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={`print-recipes-${opt.value}`}
                    disabled={opt.disabled}
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <span className="text-sm font-medium">{opt.title}</span>
                    <p className="text-xs text-muted-foreground font-normal">{opt.description}</p>
                  </div>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Stations</p>
            <div className="grid grid-cols-2 gap-2">
              {CHOICES.map(s => {
                const id = `print-station-${choiceKey(s)}`
                const n = countFor(s)
                return (
                  <Label
                    key={id}
                    htmlFor={id}
                    className="flex items-center gap-2 rounded-md border p-2.5 cursor-pointer font-normal"
                  >
                    <Checkbox
                      id={id}
                      checked={chosen.has(choiceKey(s))}
                      onCheckedChange={v => toggle(s, v === true)}
                    />
                    <span className="text-sm">{s ?? 'No station set'}</span>
                    <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                      {loading ? '…' : n}
                    </span>
                  </Label>
                )
              })}
            </div>
            {!loading && noStation.length > 0 && (
              <p className="text-xs text-muted-foreground">
                No station yet: {noStation.join(', ')}. Open a recipe to set its station.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={print} disabled={loading || printCount === 0}>
            <Printer className="w-4 h-4 mr-2" />
            Print {loading ? '' : `${printCount} recipe${printCount === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
